import { RouteParams } from "@/hooks/useRouteParams";
import { FacetsInformation, PageInformation } from "@/types/productListing";
import { StandardProps } from "@/types/types";
import { Fragment } from "react";

type MetaContentProps = {
  route: RouteParams;
  facetsInformation: FacetsInformation;
  pageInformation: PageInformation;
};

function encodeHTML(str: string) {
  return str.replace(/[\u00A0-\u9999<>]/g, function (i) {
    return "&#" + i.charCodeAt(0) + ";";
  });
}

function createSearchTitle(isFiltered: boolean, searchTerm: string = "") {
  return `${
    isFiltered ? "Filtered " : ""
  }Search results for "${searchTerm}" | John Lewis & Partners`;
}

function createSearchDescription(isFiltered: boolean, searchTerm: string = "") {
  return `${isFiltered ? "Filtered " : ""}Search results for "${searchTerm}" on John Lewis & Partners. Free delivery on orders over £70`;
}

function getMetaContent({
  facetsInformation,
  pageInformation,
  route,
}: MetaContentProps) {
  const isFiltered = facetsInformation?.isFiltered;

  if (route.pageType === "search") {
    const searchTerm = Array.isArray(route.query["search-term"])
      ? route.query["search-term"][0]
      : route.query["search-term"];
    return {
      title: createSearchTitle(isFiltered, searchTerm),
      description: createSearchDescription(isFiltered, searchTerm),
    };
  }

  return {
    title: pageInformation.title,
    description: pageInformation.description || "",
  };
}

type MetaDetailsProps = Pick<StandardProps, "productListingData"> & {
  currentPageCanonical: string;
  route: RouteParams;
};

export function MetaDetails({
  currentPageCanonical,
  productListingData,
  route,
}: MetaDetailsProps) {
  const { facetsInformation, pageInformation, products } = productListingData;
  const images = products.slice(0, 3).map((product) => product.image) || [];

  const { title, description } = getMetaContent({
    facetsInformation,
    pageInformation,
    route,
  });
  const encodedDescription = encodeHTML(description);

  return (
    <Fragment>
      {pageInformation.noIndex && (
        <meta key="metaRobots" name="robots" content="noindex" />
      )}
      <meta
        key="metaDescription"
        name="description"
        content={encodedDescription}
      />
      <meta
        key="metaOgSiteName"
        property="og:site_name"
        content="John Lewis & Partners"
      />
      <meta key="metaOgUrl" property="og:url" content={currentPageCanonical} />
      {images.length > 0 ? (
        <Fragment>
          {images.map((image) => (
            <meta
              key={`metaOgImage-${image}`}
              property="og:image"
              content={image}
            />
          ))}
          <meta
            key="metaOgImageType"
            property="og:image:type"
            content="image/jpeg"
          />
        </Fragment>
      ) : (
        <Fragment>
          <meta
            key="metaOgImage"
            property="og:image"
            content="https://media.johnlewiscontent.com/i/JohnLewis/ogFeatureImage-johnLewisPartners-040918"
          />
          <meta
            key="metaOgImageType"
            property="og:image:type"
            content="image/png"
          />
        </Fragment>
      )}
      <meta key="metaOgType" property="og:type" content="website" />
      <meta key="metaOgLocale" property="og:locale" content="en_GB" />
      <meta key="metaOgTitle" property="og:title" content={title} />
      <meta
        key="metaOgDescription"
        property="og:description"
        content={encodedDescription}
      />

      <meta key="metaHttpEquiv" httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta key="metaThemeColor" name="theme-color" content="#ffffff" />
      <meta
        key="metaTwitterCard"
        name="twitter:card"
        content="summary_large_image"
      />
      <meta
        key="metaTwitterSite"
        name="twitter:site"
        content="@JLandPartners"
      />
      <meta
        key="metaAppleItunesApp"
        name="apple-itunes-app"
        content="app-id=486502369, app-argument=m.johnlewis.com"
      />
      <meta
        key="metaMobileWebAppCapable"
        name="mobile-web-app-capable"
        content="yes"
      />
      <link
        key="metaManifest"
        rel="manifest"
        href="/static/assets/favicon/manifest.json"
      />
      <link
        key="metaFaviconIco"
        rel="shortcut icon"
        type="image/x-icon"
        href="https://www.johnlewis.com/static/assets/favicon/favicon.ico"
      />
      <link
        key="metaFaviconSvg"
        rel="icon"
        type="image/svg+xml"
        href="https://www.johnlewis.com/static/assets/favicon.svg"
      />
      <link
        key="metaAppleTouchIcon"
        rel="apple-touch-icon"
        href="https://www.johnlewis.com/static/assets/favicon/apple-touch-icon.png"
      />
    </Fragment>
  );
}
